import { useState } from "react"
import { Button, Spinner } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import { importVehicleDriveImages } from "api/vehicleStockApi"
import { useToastMessage } from "hooks/useToastMessage"

const getDrivePreview = link => {
  if (!link) return null

  const folderMatch = link.match(/\/folders\/([^/?#]+)/)
  const fileMatch = link.match(/\/file\/d\/([^/?#]+)/)
  const idMatch = link.match(/[?&]id=([^&#]+)/)
  const resourceKeyMatch = link.match(/[?&]resourcekey=([^&#]+)/)
  const id =
    (folderMatch && folderMatch[1]) ||
    (fileMatch && fileMatch[1]) ||
    (idMatch && idMatch[1])
  const resourceKey = resourceKeyMatch && resourceKeyMatch[1]
  const resourceKeyParam = resourceKey ? `&resourcekey=${resourceKey}` : ""

  if (!id) {
    return {
      url: link,
      type: "link",
    }
  }

  if (folderMatch) {
    return {
      url: `https://drive.google.com/embeddedfolderview?id=${id}${resourceKeyParam}#grid`,
      type: "folder",
    }
  }

  return {
    url: `https://drive.google.com/file/d/${id}/preview${
      resourceKey ? `?resourcekey=${resourceKey}` : ""
    }`,
    type: "file",
  }
}

const DriveImagePreviewModal = ({
  isOpen,
  toggle,
  driveLink,
  stockId,
  onImported,
}) => {
  const preview = getDrivePreview(driveLink)
  const [isImporting, setIsImporting] = useState(false)
  const { handleResponse, handleError } = useToastMessage()
  const canImport = Boolean(stockId && driveLink && preview?.type !== "link")

  const handleImport = async () => {
    if (!canImport || isImporting) return

    try {
      setIsImporting(true)
      const res = await importVehicleDriveImages({
        vehicleId: stockId,
        driveLink,
      })

      handleResponse(res, "Drive images imported and old images replaced.")

      if (res?.success) {
        onImported?.(res)
      }
    } catch (error) {
      handleError(error, "Failed to import Drive images.")
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Drive Images"
      size="xl"
      footer={
        <div className="d-flex justify-content-between w-100">
          <div className="d-flex gap-2">
            <Button
              color="primary"
              outline
              type="button"
              onClick={() => window.open(driveLink, "_blank")}
              disabled={!driveLink || isImporting}
            >
              <i className="bx bx-link-external me-1" />
              Open Drive Link
            </Button>
            <Button
              color="success"
              type="button"
              onClick={handleImport}
              disabled={!canImport || isImporting}
            >
              {isImporting ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="bx bx-import me-1" />
              )}
              {isImporting ? "Importing..." : "Replace Vehicle Images"}
            </Button>
          </div>
          <Button color="light" type="button" onClick={toggle}>
            Close
          </Button>
        </div>
      }
    >
      {!driveLink ? (
        <div className="text-center text-muted py-5">
          No Google Drive link is saved for this vehicle.
        </div>
      ) : preview && preview.type === "link" ? (
        <div className="text-center py-5">
          <p className="text-muted mb-3">
            This Drive link cannot be previewed here.
          </p>
          <Button
            color="primary"
            type="button"
            onClick={() => window.open(driveLink, "_blank")}
          >
            <i className="bx bx-link-external me-1" />
            Open Drive Link
          </Button>
        </div>
      ) : (
        <iframe
          title="Google Drive image preview"
          src={preview.url}
          className="w-100 border rounded"
          style={{ minHeight: "70vh" }}
          allow="autoplay"
        />
      )}
    </ThemeModal>
  )
}

export default DriveImagePreviewModal
